import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAppId } from '../../context/AppIdContext';
import '../../style/speakSearch.css';
import Header from '../../component/header';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const MasterSearchSlip = () => {
    // --- Context ---
    const { appId, language } = useAppId(); 
    const navigate = useNavigate();

    // --- Translation Dictionary ---
    const t = {
        en: {
            pageTitle: "Master Search",
            pageSubtitle: "Advanced lookup using multiple criteria.",
            btnSearch: "Search",
            btnReset: "Clear",
            searchAgain: "Search Again",
            cancel: "Cancel",
            loadingText: "Fetching Records...",
            noRecords: "No records found.",
            fName: "First Name",
            mName: "Middle Name",
            surname: "Surname",
            mobileInput: "Mobile Number",
            idCardInput: "Voter ID",
            fatherHusband: "F/S :",
            serialNo: "Sr No. :",
            mobile: "Mobile :",
            idCardNo: "ID Card No :",
            boothNo: "Booth No :",
            houseNo: "House No :",
            pollingLoc: "Voting Loc :",
            slipStatus: "Slip Status",
            sent: "Sent",
            notSent: "Not Sent",
            slipCount: "Times Sent",
            sentOn: "Sent On",
            call: "Call",
            text: "Text",
            slip: "Slip",
            prachar: "Prachar",
            family: "Family",
            print: "Print",
            alertNoMobile: "Mobile number not available for this voter.",
            alertFamilyError: "Could not fetch family details.",
            allVoters: "All Voters",
            sentOnly: "Sent Only",
            remainingOnly: "Remaining Only"
        },
        hi: {
            pageTitle: "मास्टर खोज",
            pageSubtitle: "कई मानदंडों का उपयोग करके उन्नत खोज।",
            btnSearch: "खोजें",
            btnReset: "साफ़ करें",
            searchAgain: "फिर से खोजें",
            cancel: "रद्द करें",
            loadingText: "रिकॉर्ड प्राप्त कर रहे हैं...",
            noRecords: "कोई रिकॉर्ड नहीं मिला।",
            fName: "पहला नाम",
            mName: "मध्य नाम",
            surname: "उपनाम",
            mobileInput: "मोबाइल नंबर",
            idCardInput: "वोटर आईडी",
            fatherHusband: "पिता/पति :",
            serialNo: "क्रमांक :",
            mobile: "मोबाइल :",
            idCardNo: "पहचान पत्र नं.:",
            boothNo: "बूथ नं :",
            houseNo: "घर नं :",
            pollingLoc: "मतदान स्थान :",
            slipStatus: "स्लिप स्थिति",
            sent: "भेजा गया",
            notSent: "भेजा नहीं गया",
            slipCount: "भेजे गए बार",
            sentOn: "भेजा गया",
            call: "कॉल",
            text: "मैसेज",
            slip: "पर्ची",
            prachar: "प्रचार",
            family: "परिवार",
            print: "प्रिन्ट",
            alertNoMobile: "इस मतदाता के लिए मोबाइल नंबर उपलब्ध नहीं है।",
            alertFamilyError: "परिवार का विवरण प्राप्त नहीं किया जा सका।",
            allVoters: "सभी मतदाता",
            sentOnly: "केवल भेजे गए",
            remainingOnly: "केवल शेष"
        },
        gu: {
            pageTitle: "માસ્ટર શોધ",
            pageSubtitle: "બહુવિધ માપદંડોનો ઉપયોગ કરીને અદ્યતન શોધ.",
            btnSearch: "શોધો",
            btnReset: "સાફ કરો",
            searchAgain: "ફરીથી શોધો",
            cancel: "રદ કરો",
            loadingText: "રેકોર્ડ મેળવી રહ્યા છે...",
            noRecords: "કોઈ રેકોર્ડ મળ્યા નથી.",
            fName: "પ્રથમ નામ",
            mName: "મધ્ય નામ",
            surname: "અટક",
            mobileInput: "મોબાઇલ નંબર",
            idCardInput: "મતદાર આઈડી",
            fatherHusband: "પિતા/પતિ :",
            serialNo: "ક્રમાંક :",
            mobile: "મોબાઇલ :",
            idCardNo: "ઓળખ કાર્ડ નં :",
            boothNo: "બૂથ નં :",
            houseNo: "ઘર નં :",
            pollingLoc: "મતદાન સ્થળ :",
            slipStatus: "સ્લિપ સ્થિતિ",
            sent: "મોકલેલ",
            notSent: "મોકલેલ નથી",
            slipCount: "મોકલેલ વખત",
            sentOn: "મોકલ્યા તારીખ",
            call: "કૉલ",
            text: "સંદેશ",
            slip: "સ્લિપ",
            prachar: "પ્રચાર",
            family: "કુટુંબ",
            print: "Print",
            alertNoMobile: "આ મતદાર માટે મોબાઇલ નંબર ઉપલબ્ધ નથી.",
            alertFamilyError: "કુટુંબની વિગતો મેળવી શકાઈ નહીં.",
            allVoters: "બધા મતદારો",
            sentOnly: "માત્ર મોકલેલ",
            remainingOnly: "માત્ર બાકી"
        }
    };

    const txt = t[language || 'en'];

    // --- State ---
    const [searchParams, setSearchParams] = useState({
        f_name: "",
        m_name: "",
        surname: "",
        mobile_no: "",
        id_card_no: ""
    });
    const [voters, setVoters] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    
    // --- Filter State ---
    const [filterType, setFilterType] = useState('all');
    const [showFilterDropdown, setShowFilterDropdown] = useState(false);
    
    // --- Modal State ---
    const [isSearchModalOpen, setIsSearchModalOpen] = useState(true);

    // --- Pagination State ---
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 50;

    useEffect(() => {
        if (!appId) {
            navigate("/");
        }
    }, [appId, navigate]);

    const handleChange = (e) => {
        setSearchParams({ ...searchParams, [e.target.name]: e.target.value });
    };

    const handleReset = () => {
        setSearchParams({ f_name: "", m_name: "", surname: "", mobile_no: "", id_card_no: "" });
    };

    const handleModalClose = () => {
        if (hasSearched) {
            setIsSearchModalOpen(false);
        } else {
            navigate(-1);
        }
    };

    const fetchVoters = async (e) => {
        if (e) e.preventDefault();

        const { f_name, m_name, surname, mobile_no, id_card_no } = searchParams;
        if (!f_name && !m_name && !surname && !mobile_no && !id_card_no) {
            alert("Please enter at least one search criteria.");
            return;
        }

        setIsSearchModalOpen(false);
        setLoading(true);
        setCurrentPage(1);
        setHasSearched(true);

        try {
            const response = await axios.post(`${BASE_URL}/api/master_search_for_slip_send`, {
                app_id: appId,
                f_name: f_name.trim(),
                m_name: m_name.trim(),
                surname: surname.trim(),
                mobile_no: mobile_no.trim(),
                id_card_no: id_card_no.trim()
            });

            if (response.data.success && response.data.data && response.data.data.result) {
                setVoters(response.data.data.result);
            } else {
                setVoters([]);
            }
        } catch (error) {
            console.error("Error fetching voters:", error);
            setVoters([]);
        } finally {
            setLoading(false);
        }
    };

    const filteredVoters = voters.filter(voter => {
        if (filterType === 'sent') {
            return voter.slip_send === 1;
        } else if (filterType === 'remaining') {
            return voter.slip_send === 0;
        }
        return true;
    });

    const sentCount = voters.filter(v => v.slip_send === 1).length;
    const remainingCount = voters.filter(v => v.slip_send === 0).length;

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentVoters = filteredVoters.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredVoters.length / itemsPerPage);

    const paginate = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const getPageNumbers = () => {
        const pageNumbers = [];
        const maxVisibleButtons = 5;
        if (totalPages <= maxVisibleButtons) {
            for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
        } else {
            pageNumbers.push(1);
            let startPage = Math.max(2, currentPage - 1);
            let endPage = Math.min(totalPages - 1, currentPage + 1);
            if (currentPage <= 3) endPage = 4;
            if (currentPage >= totalPages - 2) startPage = totalPages - 3;
            if (startPage > 2) pageNumbers.push('...');
            for (let i = startPage; i <= endPage; i++) pageNumbers.push(i);
            if (endPage < totalPages - 1) pageNumbers.push('...');
            pageNumbers.push(totalPages);
        }
        return pageNumbers;
    };

    const getActiveFilterLabel = () => {
        if (filterType === 'sent') return txt.sentOnly;
        if (filterType === 'remaining') return txt.remainingOnly;
        return txt.allVoters;
    };

    const handleWhatsAppShare = async (voter) => {
        if (!voter.contact_no || voter.contact_no === "-") {
            alert(txt.alertNoMobile);
            return;
        }

        try {
            const response = await axios.post(`${BASE_URL}/api/get_receipt_in_all_family_for_wtsp`, {
                app_id: appId,
                voter_id: voter.id,
                user_id: appId
            });

            if (response.data.success && response.data.data && response.data.data.result) {
                const familyMembers = response.data.data.result;
                let message = "";
                familyMembers.forEach((member, index) => {
                    message += `${member.first_detail.replace(/\s+/g, ' ')}\n`;
                    message += `${member.full_name}\n`;
                    message += `${member.middle_name}\n`;
                    message += `${member.sex_age.replace('स्री', 'स्त्री')}\n`;
                    message += `${member.idcard_no}\n\n`;
                    message += `${language === 'hi' ? 'मतदान स्थल :' : 'Voting Loc :'}\n`;
                    message += `${member.eng_polling_location}\n\n`;
                    if (index < familyMembers.length - 1) {
                        message += `----------------------------------------\n\n`;
                    }
                });

                const encodedMessage = encodeURIComponent(message);
                let mobile = voter.contact_no.replace(/\D/g, '');
                if (mobile.length === 10) mobile = '91' + mobile;
                window.open(`https://wa.me/${mobile}?text=${encodedMessage}`, '_blank');
            } else {
                alert(txt.alertFamilyError);
            }
        } catch (error) {
            alert(txt.alertFamilyError);
        }
    };

    return (
        <div className='app-container p-4'>

            <Header title={txt.pageTitle} />

            {/* --- SEARCH AGAIN BUTTON HEADER --- */}
            {hasSearched && !loading && (
                <div className="search-header-wrapper">
                    <div className="results-count">
                        {filteredVoters.length > 0 ? `Showing ${filteredVoters.length} results` : ""}
                    </div>
                    <div className="action-buttons">
                        {/* Filter Button - Fixed Position */}
                        <div className="filter-button-container">
                            <button 
                                className="filter-trigger-btn"
                                onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polygon points="22 3 2 3 10 13 10 21 14 18 14 13 22 3" />
                                </svg>
                                <span className="filter-badge">{getActiveFilterLabel()} ({filterType === 'all' ? voters.length : filterType === 'sent' ? sentCount : remainingCount})</span>
                            </button>

                            {showFilterDropdown && (
                                <div className="filter-dropdown">
                                    <button 
                                        className={`filter-option ${filterType === 'all' ? 'active' : ''}`}
                                        onClick={() => {
                                            setFilterType('all');
                                            setShowFilterDropdown(false);
                                            setCurrentPage(1);
                                        }}
                                    >
                                        <span className="filter-dot all"></span>
                                        {txt.allVoters} ({voters.length})
                                    </button>
                                    <button 
                                        className={`filter-option ${filterType === 'sent' ? 'active' : ''}`}
                                        onClick={() => {
                                            setFilterType('sent');
                                            setShowFilterDropdown(false);
                                            setCurrentPage(1);
                                        }}
                                    >
                                        <span className="filter-dot sent"></span>
                                        {txt.sentOnly} ({sentCount})
                                    </button>
                                    <button 
                                        className={`filter-option ${filterType === 'remaining' ? 'active' : ''}`}
                                        onClick={() => {
                                            setFilterType('remaining');
                                            setShowFilterDropdown(false);
                                            setCurrentPage(1);
                                        }}
                                    >
                                        <span className="filter-dot remaining"></span>
                                        {txt.remainingOnly} ({remainingCount})
                                    </button>
                                </div>
                            )}
                        </div>
                        <button 
                            className="btn-search-modern" 
                            onClick={() => setIsSearchModalOpen(true)}
                        >
                            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" className="me-1"><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                            {txt.searchAgain}
                        </button>
                    </div>
                </div>
            )}

            {/* --- MODAL POPUP FOR SEARCH --- */}
            {isSearchModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content modern-card slide-up-element p-3">
                        <div className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-3">
                            <div>
                                <h4 className="m-0 fw-bold text-slate">{txt.pageTitle}</h4>
                                <span className="text-muted" style={{fontSize: '0.85rem'}}>{txt.pageSubtitle}</span>
                            </div>
                            <button className="btn-close-custom" onClick={handleModalClose}>✕</button>
                        </div>
                        <form onSubmit={fetchVoters}>
                            <div className="row g-3">
                                <div className="col-12 col-md-4">
                                    <label className="search-label">{txt.fName}</label>
                                    <input type="text" name="f_name" value={searchParams.f_name} onChange={handleChange} className="search-input-box" placeholder={txt.fName} />
                                </div>
                                <div className="col-12 col-md-4">
                                    <label className="search-label">{txt.mName}</label>
                                    <input type="text" name="m_name" value={searchParams.m_name} onChange={handleChange} className="search-input-box" placeholder={txt.mName} />
                                </div>
                                <div className="col-12 col-md-4">
                                    <label className="search-label">{txt.surname}</label>
                                    <input type="text" name="surname" value={searchParams.surname} onChange={handleChange} className="search-input-box" placeholder={txt.surname} />
                                </div>
                                <div className="col-12 col-md-6">
                                    <label className="search-label">{txt.mobileInput}</label>
                                    <input type="text" name="mobile_no" value={searchParams.mobile_no} onChange={handleChange} className="search-input-box" placeholder="e.g. 9876543210" />
                                </div>
                                <div className="col-12 col-md-6">
                                    <label className="search-label">{txt.idCardInput}</label>
                                    <input type="text" name="id_card_no" value={searchParams.id_card_no} onChange={handleChange} className="search-input-box" placeholder="e.g. ABC1234567" />
                                </div>
                                
                                <div className="col-12 d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
                                    <button type="button" onClick={handleReset} className="btn-reset-modern">
                                        {txt.btnReset}
                                    </button>
                                    <button type="submit" className="btn-search-modern">
                                        {txt.btnSearch}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* --- Loading State --- */}
            {loading && (
                <div className="loader-wrapper">
                    <div className="spinner"></div>
                    <p className="loader-text">{txt.loadingText}</p>
                </div>
            )}

            {/* --- No Records Found --- */}
            {!loading && hasSearched && filteredVoters.length === 0 && !isSearchModalOpen && (
                <div className="text-center text-muted p-5 modern-card slide-up-element mt-3">
                    <h4>{txt.noRecords}</h4>
                </div>
            )}

            {/* --- RESULTS GRID --- */}
            <div className="row g-4 pb-5 mt-1">
                {!loading && currentVoters.map((voter, index) => {
                    const isSent = voter.slip_send === 1;
                    return (
                        <div className="col-12 col-md-6 col-lg-4 slide-up-element" key={voter.id || index} style={{ animationDelay: `${index * 0.05}s` }}>
                            <div className="voter-card-clean h-100">
                                <div className="voter-header">
                                    <span className="serial-number">{indexOfFirstItem + index + 1}.</span>
                                    <span className="voter-name-bold ms-2">{voter.eng_f_name} {voter.eng_surname}</span>
                                    <span className={`status-badge ${isSent ? 'badge-sent' : 'badge-remaining'}`}>
                                        {isSent ? txt.sent : txt.notSent}
                                    </span>
                                </div>

                                <div className="voter-body position-relative">
                                    <div className="map-icon-wrapper"><img src="https://upload.wikimedia.org/wikipedia/commons/a/aa/Google_Maps_icon_%282020%29.svg" alt="Map" width="15" /></div>
                                    <div className="info-row"><span className="label">{txt.fatherHusband}</span><span className="value">{voter.eng_m_name || "N/A"}</span></div>
                                    <div className="info-row"><span className="label">{txt.serialNo}</span><span className="value">{voter.slnoinpart || voter.serial_no || "N/A"}</span></div>
                                    <div className="info-row"><span className="label">{txt.mobile}</span><span className="value mobile-value">{voter.contact_no || "N/A"}</span></div>
                                    <div className="info-row"><span className="label">{txt.idCardNo}</span><span className="value">{voter.idcard_no || "N/A"}</span></div>
                                    <div className="info-row"><span className="label">{txt.boothNo}</span><span className="value">{voter.part_no || voter.booth_no || "N/A"}</span></div>
                                    <div className="info-row"><span className="label">{txt.houseNo}</span><span className="value">{voter.eng_house_no || "N/A"}</span></div>
                                    <div className="info-row"><span className="label">{txt.pollingLoc}</span><span className="value" title={voter.eng_polling_location}>{voter.eng_polling_location || "-"}</span></div>
                                    
                                    {/* Slip Status Details */}
                                    <div className="slip-status-section mt-2 pt-2" style={{ borderTop: '1px dashed #e2e8f0' }}>
                                        <div className="info-row mb-1">
                                            <span className="label">{txt.slipStatus}</span>
                                            <span className={`value fw-bold ${isSent ? 'text-success' : 'text-warning'}`}>
                                                {isSent ? txt.sent : txt.notSent}
                                            </span>
                                        </div>
                                        <div className="info-row mb-1">
                                            <span className="label">{txt.slipCount}</span>
                                            <span className="value">
                                                {voter.sleep_count !== null && voter.sleep_count !== undefined ? voter.sleep_count : 0}
                                            </span>
                                        </div>
                                        <div className="info-row">
                                            <span className="label">{txt.sentOn}</span>
                                            <span className="value text-success fw-bold">
                                                {voter.send_date ? voter.send_date : "-"}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="voter-actions">
                                    <ActionButton icon="📞" color="#0ea5e9" label={txt.call} link={`tel:${voter.contact_no}`} />
                                    <ActionButton icon="✉️" color="#f59e0b" label={txt.text} link={`sms:${voter.contact_no}`} />
                                    <button onClick={() => handleWhatsAppShare(voter)} className="action-btn-wrapper" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                                        <div className="circle-btn" style={{ backgroundColor: '#22c55e' }}>💬</div>
                                        <span className="btn-label">{txt.slip}</span>
                                    </button>
                                    <ActionButton icon="👤" color="#ef4444" label={txt.prachar} />
                                    <ActionButton
                                        icon="🖨"
                                        color="#f97316"
                                        label={txt.print}
                                        onClick={() => navigate('/printer', { state: { voter_id: voter.id } })}
                                    />
                                    <ActionButton icon="👨‍👩‍👧" color="#fbbf24" label={txt.family} />
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* --- PAGINATION --- */}
            {!loading && filteredVoters.length > itemsPerPage && !isSearchModalOpen && (
                <div className="pagination-wrapper">
                    <div className="pagination-content">
                        <button className="pagination-nav-btn" onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1}>
                            <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
                        </button>
                        <div className="page-numbers">
                            {getPageNumbers().map((number, index) => (
                                <React.Fragment key={index}>
                                    {number === '...' ? (<span className="page-dots">...</span>) : (
                                        <button className={`page-num-btn ${currentPage === number ? 'active' : ''}`} onClick={() => paginate(number)}>{number}</button>
                                    )}
                                </React.Fragment>
                            ))}
                        </div>
                        <button className="pagination-nav-btn" onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages}>
                            <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                        </button>
                    </div>
                </div>
            )}

            <style jsx>{`
                .app-container { background: #E5E7EB; min-height: 100vh; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; padding-bottom: 80px; }
                @keyframes slideInUp { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }
                .slide-up-element { opacity: 0; animation: slideInUp 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
                
                /* Search Header - FIXED */
                .search-header-wrapper {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-top: 20px;
                    margin-bottom: 20px;
                    flex-wrap: wrap;
                    gap: 12px;
                    position: relative;
                    z-index: 10;
                }
                
                .results-count {
                    font-size: 14px;
                    font-weight: 600;
                    color: #475569;
                }
                
                .action-buttons {
                    display: flex;
                    gap: 12px;
                    align-items: center;
                }
                
                /* Status Badge Styles */
                .status-badge {
                    font-size: 10px;
                    font-weight: 700;
                    padding: 4px 10px;
                    border-radius: 20px;
                    margin-left: auto;
                }
                .badge-sent {
                    background: #d1fae5;
                    color: #065f46;
                }
                .badge-remaining {
                    background: #fed7aa;
                    color: #9a3412;
                }
                
                /* Filter Button Styles - FIXED POSITION */
                .filter-button-container {
                    position: relative;
                    display: inline-block;
                }

                .filter-trigger-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    padding: 8px 16px;
                    background: white;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    font-weight: 500;
                    font-size: 13px;
                    color: #475569;
                    white-space: nowrap;
                }

                .filter-trigger-btn:hover {
                    background: #f8fafc;
                    border-color: #cbd5e1;
                }

                .filter-badge {
                    background: #f1f5f9;
                    padding: 4px 8px;
                    border-radius: 6px;
                    font-size: 11px;
                    font-weight: 600;
                    color: #2563eb;
                }

                /* Dropdown positioned above content */
                .filter-dropdown {
                    position: absolute;
                    top: 100%;
                    right: 0;
                    margin-top: 8px;
                    background: white;
                    border: 1px solid #e2e8f0;
                    border-radius: 12px;
                    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);
                    min-width: 180px;
                    z-index: 1000;
                    overflow: hidden;
                    animation: fadeIn 0.2s ease;
                }

                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(-10px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                .filter-option {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    width: 100%;
                    padding: 12px 16px;
                    border: none;
                    background: white;
                    cursor: pointer;
                    font-size: 13px;
                    font-weight: 500;
                    color: #475569;
                    transition: all 0.2s ease;
                    text-align: left;
                }

                .filter-option:hover {
                    background: #f8fafc;
                }

                .filter-option.active {
                    background: #eff6ff;
                    color: #2563eb;
                }

                .filter-dot {
                    width: 8px;
                    height: 8px;
                    border-radius: 50%;
                    display: inline-block;
                }

                .filter-dot.all {
                    background: #3b82f6;
                }

                .filter-dot.sent {
                    background: #10b981;
                }

                .filter-dot.remaining {
                    background: #f59e0b;
                }
                
                /* Slip Status Section */
                .slip-status-section{
                    border-radius: 8px;
                    padding: 8px;
                    margin-top: 8px;
                }
                .slip-status-section .info-row {
                    margin-bottom: 6px;
                }
                .slip-status-section .info-row:last-child {
                    margin-bottom: 0;
                }
                
                .text-success { color: #059669 !important; }
                .text-warning { color: #d97706 !important; }
                .fw-bold { font-weight: 700 !important; }
                
                /* --- MODAL CSS --- */
                .modal-overlay {
                    position: fixed; top: 0; left: 0; right: 0; bottom: 0;
                    background: rgba(15, 23, 42, 0.7);
                    backdrop-filter: blur(5px);
                    display: flex; justify-content: center; align-items: center;
                    z-index: 1050; padding: 20px;
                }
                .modal-content {
                    width: 100%; max-width: 800px;
                    max-height: 90vh; overflow-y: auto;
                }
                .btn-close-custom {
                    background: none; border: none; font-size: 1.5rem; color: #64748B; cursor: pointer; line-height: 1; transition: color 0.2s;
                }
                .btn-close-custom:hover { color: #0F172A; }

                /* --- FORM CSS --- */
                .modern-card { background: #FFFFFF; border-radius: 16px; box-shadow: 0 10px 25px -5px rgba(0,0,0,0.1); border: 1px solid #E2E8F0; }
                .search-label { display: block; font-size: 0.85rem; font-weight: 600; color: #475569; margin-bottom: 6px; }
                .search-input-box { width: 100%; padding: 10px 14px; border: 1px solid #CBD5E1; border-radius: 8px; font-size: 0.95rem; color: #1E293B; transition: all 0.2s; outline: none; }
                .search-input-box:focus { border-color: #8B5CF6; box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1); }
                
                .btn-search-modern { display: inline-flex; align-items: center; justify-content: center; background: #2563EB; color: white; border: none; padding: 8px 20px; border-radius: 8px; font-weight: 600; font-size: 13px; cursor: pointer; transition: 0.2s; }
                .btn-search-modern:hover:not(:disabled) { background: #1D4ED8; transform: translateY(-1px); box-shadow: 0 4px 6px rgba(37,99,235,0.2); }
                .btn-reset-modern { display: inline-flex; align-items: center; justify-content: center; background: #F1F5F9; color: #475569; border: 1px solid #CBD5E1; padding: 8px 20px; border-radius: 8px; font-weight: 600; font-size: 13px; cursor: pointer; transition: 0.2s; }

                /* --- LOADER --- */
                .loader-wrapper { display: flex; flex-direction: column; justify-content: center; align-items: center; min-height: 30vh; width: 100%; }
                .spinner { width: 40px; height: 40px; border: 4px solid #E5E7EB; border-top: 4px solid #2563EB; border-radius: 50%; animation: spin 1s linear infinite; margin-bottom: 12px; }
                .loader-text { color: #6B7280; font-weight: 500; }
                @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
                
                /* --- PAGINATION --- */
                .pagination-wrapper { display: flex; justify-content: center; padding: 20px 0; position: fixed; bottom: 0; left: 0; width: 100%; z-index: 1000; pointer-events: none; }
                .pagination-content { background: white; border: 1px solid #E2E8F0; border-radius: 50px; padding: 8px 16px; display: flex; align-items: center; gap: 10px; box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1); pointer-events: auto; }
                .pagination-nav-btn { display: flex; align-items: center; justify-content: center; width: 36px; height: 36px; border-radius: 50%; background: transparent; border: none; color: #2563EB; cursor: pointer; }
                .pagination-nav-btn:disabled { color: #CBD5E1; cursor: not-allowed; }
                .page-numbers { display: flex; gap: 5px; align-items: center; }
                .page-num-btn { width: 32px; height: 32px; border-radius: 50%; border: 1px solid transparent; background: transparent; color: #475569; font-weight: 600; font-size: 14px; cursor: pointer; }
                .page-num-btn.active { background-color: #2563EB; color: white; border-color: #2563EB; }
                .page-dots { color: #94A3B8; font-weight: bold; padding: 0 4px; }
                
                /* Mobile Optimization */
                @media (max-width: 640px) {
                    .search-header-wrapper {
                        flex-direction: column;
                        align-items: stretch;
                    }
                    .action-buttons {
                        justify-content: flex-end;
                    }
                    .filter-trigger-btn {
                        padding: 6px 12px;
                        font-size: 12px;
                    }
                    .filter-badge {
                        font-size: 10px;
                        padding: 2px 6px;
                    }
                    .btn-search-modern {
                        padding: 6px 16px;
                        font-size: 12px;
                    }
                }
            `}</style>
        </div>
    );
};

// Reusable ActionButton
const ActionButton = ({ icon, color, label, link, onClick }) => {
    if (onClick) {
        return (
            <button onClick={onClick} className="action-btn-wrapper" style={{ textDecoration: 'none', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                <div className="circle-btn" style={{ backgroundColor: color }}>{icon}</div>
                <span className="btn-label">{label}</span>
            </button>
        );
    }
    
    return (
        <a href={link || "#"} className="action-btn-wrapper" style={{ textDecoration: 'none' }} target="_blank" rel="noreferrer">
            <div className="circle-btn" style={{ backgroundColor: color }}>{icon}</div>
            <span className="btn-label">{label}</span>
        </a>
    );
};

export default MasterSearchSlip;